/**
 * UserPreferences - Global context for persisting user grid preferences (filters, sort, columns, pageCount).
 *
 * Loads once from the API on mount, stores in-memory, and saves to the API only when explicitly triggered.
 * Each screen stores its own preferences under a unique screen key (e.g., "CONTACTS", "HOME").
 *
 * Usage:
 *   // In a page component:
 *   const { getPreferences, savePreferences } = useContext(UserPreferencesContext);
 *   const prefs = getPreferences('CONTACTS');
 *   // prefs => { filters: {}, sort: {}, hiddenColumns: {}, pageCount: 20 }
 *
 *   savePreferences('CONTACTS', { filters, sort, hiddenColumns, pageCount });
 */
import React, { createContext, useState, useCallback, useEffect, useContext, useRef } from 'react';
import axios from './AxiosConfig';
import { AppContext } from '../../RoutesWithAuth';

export const UserPreferencesContext = createContext();

const EMPTY_PREFS = { filters: {}, sort: {}, hiddenColumns: {}, pageCount: 20 };

export function UserPreferencesProvider({ children }) {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const [preferences, setPreferences] = useState({});
    const [loaded, setLoaded] = useState(false);
    const preferencesRef = useRef(preferences);

    // Keep ref in sync so save always uses latest
    useEffect(() => {
        preferencesRef.current = preferences;
    }, [preferences]);

    // Load preferences once when auth token is available
    useEffect(() => {
        if (!authToken || loaded) return;
        axios.get('/users/user-preferences', {
            headers: { Authorization: authToken }
        }).then(res => {
            if (res.data?.preferences && typeof res.data.preferences === 'object') {
                setPreferences(res.data.preferences);
            }
            setLoaded(true);
        }).catch(() => {
            // If endpoint doesn't exist yet or fails, start with empty preferences
            setLoaded(true);
        });
    }, [authToken, loaded]);

    // Get preferences for a specific screen
    const getPreferences = useCallback((screenKey) => {
        return preferences[screenKey] || EMPTY_PREFS;
    }, [preferences]);

    // Save preferences for a specific screen — updates in-memory and persists to API
    const savePreferences = useCallback((screenKey, screenPrefs) => {
        const updated = { ...preferencesRef.current, [screenKey]: screenPrefs };
        setPreferences(updated);
        preferencesRef.current = updated;

        return axios.post('/users/user-preferences', { preferences: updated }, {
            headers: { Authorization: authToken }
        }).catch(err => {
            console.error('Failed to save user preferences:', err);
            throw err;
        });
    }, [authToken]);

    // Clear preferences for a specific screen
    const clearPreferences = useCallback((screenKey) => {
        const updated = { ...preferencesRef.current };
        delete updated[screenKey];
        setPreferences(updated);
        preferencesRef.current = updated;

        return axios.post('/users/user-preferences', { preferences: updated }, {
            headers: { Authorization: authToken }
        }).catch(err => {
            console.error('Failed to clear user preferences:', err);
            throw err;
        });
    }, [authToken]);

    return (
        <UserPreferencesContext.Provider value={{ getPreferences, savePreferences, clearPreferences, loaded }}>
            {children}
        </UserPreferencesContext.Provider>
    );
}
